import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About Us" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Location & Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = LINKS.map((link) => document.getElementById(link.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-35% 0px -60% 0px" }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled || open
          ? "border-b border-[rgba(201,162,75,0.25)] bg-[#383D33]/95 shadow-lg shadow-black/20 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#home" data-testid="nav-brand-link" className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Her Styles Ladies Salon logo"
            data-testid="nav-logo-image"
            className="h-12 w-12 object-contain"
          />
          <span className="leading-tight">
            <span className="block font-brand text-lg tracking-[0.22em] text-[#F2EFE6]">
              HER STYLES
            </span>
            <span className="block font-body text-[10px] font-medium uppercase tracking-[0.42em] text-[#C9A24B]">
              Ladies Salon
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links-desktop">
          {LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              data-testid={`nav-link-${link.id}`}
              className={`relative py-1 font-body text-xs font-medium uppercase tracking-[0.22em] transition-colors duration-300 hover:text-[#C9A24B] ${
                active === link.id ? "text-[#C9A24B]" : "text-[#F2EFE6]/85"
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-0.5 left-1/2 h-px -translate-x-1/2 bg-[#C9A24B] transition-[width] duration-300 ${
                  active === link.id ? "w-full" : "w-0"
                }`}
              />
            </a>
          ))}
          <a
            href="#book"
            data-testid="nav-book-now-button"
            className="btn-shimmer rounded-full bg-[#C9A24B] px-6 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#383D33] transition-colors duration-300 hover:bg-[#D4AF37]"
          >
            Book Now
          </a>
        </nav>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="nav-mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(201,162,75,0.4)] text-[#C9A24B] lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <nav
          data-testid="nav-links-mobile"
          className="border-t border-[rgba(201,162,75,0.2)] bg-[#383D33]/98 px-6 pb-8 pt-4 backdrop-blur-md lg:hidden"
        >
          {LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              data-testid={`nav-mobile-link-${link.id}`}
              onClick={() => setOpen(false)}
              className={`block border-b border-[rgba(201,162,75,0.12)] py-4 font-body text-sm font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:text-[#C9A24B] ${
                active === link.id ? "text-[#C9A24B]" : "text-[#F2EFE6]/90"
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#book"
            data-testid="nav-mobile-book-now-button"
            onClick={() => setOpen(false)}
            className="btn-shimmer mt-6 block rounded-full bg-[#C9A24B] py-3.5 text-center font-body text-sm font-semibold uppercase tracking-[0.2em] text-[#383D33] transition-colors duration-300 hover:bg-[#D4AF37]"
          >
            Book Now
          </a>
        </nav>
      )}
    </header>
  );
};
